import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';
import { PIEZAS, FONDOS, MARCOS } from './estanque/piezas';

type CellHint = 'water' | 'sub' | 'left' | 'right' | 'top' | 'bottom' | 'middle';

interface EstanquePuzzle {
  size: number;
  fleet: number[];
  rowCounts: number[];
  colCounts: number[];
  revealed: (CellHint | null)[];
  seed: number;
  difficulty: number;
}

const difficulties = [
  { orden: 1, nombre: 'Neurona', icono: '🧠' },
  { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
  { orden: 3, nombre: 'Cerebro', icono: '🧩' },
  { orden: 4, nombre: 'Genio', icono: '💡' },
  { orden: 5, nombre: 'Einstein', icono: '🎓' },
];

// null = casilla intacta, true = el jugador cree que hay algo,
// false = descartada (sabe que ahí no hay nada). Un toque rota entre los tres.
type CellState = null | boolean;

/** Colores del tablero (8.2 de la especificación del diseñador). */
const MARCO_TABLERO = '#3A5560';
const REJILLA = '#BDD0D7';

/** A partir de este tamaño de casilla se usan las piezas con detalle. */
const PX_DETALLE = 40;
const PAD_MARCO = 10;

/**
 * La leyenda va al lado del tablero o debajo, según dónde sobre sitio: en
 * apaisado el hueco está a los lados, en vertical debajo. No se decide con un
 * breakpoint sino probando las dos y quedándose con la que deja el tablero
 * más grande, que es justo lo que el jugador quiere mirar.
 */
const ANCHO_PANEL = 190;
const ALTO_LEYENDA = 86;
const HUECO_LEYENDA = 10;

// ------------------------------------------------------------------ piezas

/**
 * Qué dibujar en una casilla ocupada, mirando a sus vecinas.
 *
 * Un extremo solo toma su forma definitiva (cabeza o cola) cuando lo que hay
 * al otro lado ya está cerrado: agua, descarte o borde. Mientras ese lado
 * siga sin decidir se queda en "sin-forma", porque el pez todavía puede
 * crecer y dibujarle la cola afirmaría un largo que el jugador aún no ha
 * deducido. Es la misma regla que ya se aplicaba a la casilla suelta, que no
 * se vuelve nenúfar hasta estar cerrada por los cuatro lados.
 *
 * Las casillas centrales sí se dibujan como cuerpo aunque el tramo siga
 * abierto: tener vecina marcada a ambos lados ya las hace cuerpo con certeza.
 */
function claveDePieza(
  puzzle: EstanquePuzzle,
  cells: CellState[],
  index: number,
  detalle: 'grande' | 'simplificada'
): string | null {
  const { size } = puzzle;
  const r = Math.floor(index / size);
  const c = index % size;
  if (cells[index] !== true) return null;

  // En la versión grande el cuerpo tiene tres variantes de manchas; la variante
  // depende de la casilla, así que no cambia mientras el pez crece.
  const central = (orient: 'horizontal' | 'vertical'): string =>
    detalle === 'grande'
      ? `grande/${orient}/central-${['a', 'b', 'c'][(c * 7 + r * 13) % 3]}`
      : `simplificada/${orient}/central`;

  const ocupada = (rr: number, cc: number): boolean =>
    rr >= 0 && rr < size && cc >= 0 && cc < size && cells[rr * size + cc] === true;
  // "Cierra" = casilla descartada, agua revelada o fuera del tablero.
  const cierra = (rr: number, cc: number): boolean =>
    rr < 0 || rr >= size || cc < 0 || cc >= size || cells[rr * size + cc] === false;

  const izq = ocupada(r, c - 1);
  const der = ocupada(r, c + 1);
  const arr = ocupada(r - 1, c);
  const aba = ocupada(r + 1, c);

  // Las casillas reveladas se dibujan con la pieza que indica la pista
  const pista = puzzle.revealed[index];
  if (pista && pista !== 'water') {
    switch (pista) {
      case 'sub': return `${detalle}/nenufar`;
      case 'left': return `${detalle}/horizontal/cabeza`;
      case 'right': return `${detalle}/horizontal/cola`;
      case 'top': return `${detalle}/vertical/cabeza`;
      case 'bottom': return `${detalle}/vertical/cola`;
      case 'middle':
        // La orientación del pez si ya se conoce; si no, horizontal.
        return central(arr || aba ? 'vertical' : 'horizontal');
    }
  }

  const horizontal = izq || der;
  const vertical = arr || aba;

  // Forma de L o T: eso no es un pez
  if (horizontal && vertical) return `${detalle}/sin-forma`;

  if (horizontal) {
    if (!izq) return cierra(r, c - 1) ? `${detalle}/horizontal/cabeza` : `${detalle}/sin-forma`;
    if (!der) return cierra(r, c + 1) ? `${detalle}/horizontal/cola` : `${detalle}/sin-forma`;
    return central('horizontal');
  }
  if (vertical) {
    if (!arr) return cierra(r - 1, c) ? `${detalle}/vertical/cabeza` : `${detalle}/sin-forma`;
    if (!aba) return cierra(r + 1, c) ? `${detalle}/vertical/cola` : `${detalle}/sin-forma`;
    return central('vertical');
  }

  // Suelta: es un nenúfar solo si está cerrada por los cuatro lados; mientras
  // siga abierta no tiene forma todavía, porque aún puede crecer.
  const cerrada =
    cierra(r - 1, c) && cierra(r + 1, c) && cierra(r, c - 1) && cierra(r, c + 1);
  return cerrada ? `${detalle}/nenufar` : `${detalle}/sin-forma`;
}

// ------------------------------------------------------------- reglas vivas

/**
 * ¿El grupo está cerrado por agua (o por el borde) en sus dos extremos?
 * Mientras no lo esté, ese trozo todavía puede crecer: no es un pez hecho y no
 * debe descontarse de la leyenda. Una casilla cuenta como agua tanto si el
 * jugador la ha descartado como si su fila o columna ya está completa.
 */
/** Forma de la casilla `i` tal como la ha dibujado el jugador. */
function formaEn(cells: CellState[], size: number, i: number): CellHint {
  const r = Math.floor(i / size);
  const c = i % size;
  const pez = (rr: number, cc: number): boolean =>
    rr >= 0 && rr < size && cc >= 0 && cc < size && cells[rr * size + cc] === true;

  if (!pez(r, c)) return 'water';
  const arriba = pez(r - 1, c);
  const abajo = pez(r + 1, c);
  const izq = pez(r, c - 1);
  const der = pez(r, c + 1);
  if (!arriba && !abajo && !izq && !der) return 'sub';
  if ((izq && der) || (arriba && abajo)) return 'middle';
  if (der) return 'left';
  if (izq) return 'right';
  if (abajo) return 'top';
  return 'bottom';
}

/**
 * ¿Está resuelto? Como el puzzle tiene SOLUCIÓN ÚNICA, un tablero que cumple
 * todas las reglas es necesariamente el correcto. Por eso el navegador no
 * necesita conocer la solución para saber que el jugador ha acertado — y así
 * no se la puede mirar quien quiera hacerse trampas.
 */
function tableroResuelto(puzzle: EstanquePuzzle, cells: CellState[]): boolean {
  const { size, rowCounts, colCounts, revealed, fleet } = puzzle;
  const pez = (r: number, c: number): boolean =>
    r >= 0 && r < size && c >= 0 && c < size && cells[r * size + c] === true;

  // los números de cada fila y columna
  for (let r = 0; r < size; r++) {
    let n = 0;
    for (let c = 0; c < size; c++) if (pez(r, c)) n++;
    if (n !== rowCounts[r]) return false;
  }
  for (let c = 0; c < size; c++) {
    let n = 0;
    for (let r = 0; r < size; r++) if (pez(r, c)) n++;
    if (n !== colCounts[c]) return false;
  }

  // ningún contacto en diagonal
  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size; c++) {
      if (!pez(r, c)) continue;
      if (pez(r + 1, c - 1) || pez(r + 1, c + 1)) return false;
    }
  }

  // piezas rectas y flota exacta
  const visto = new Array<boolean>(size * size).fill(false);
  const tamanos: number[] = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (!pez(r, c) || visto[r * size + c]) continue;
      const horizontal = pez(r, c + 1);
      const vertical = pez(r + 1, c);
      if (horizontal && vertical) return false;      // forma en L o en T
      let largo = 1;
      visto[r * size + c] = true;
      if (horizontal) {
        let cc = c + 1;
        while (pez(r, cc)) { visto[r * size + cc] = true; largo++; cc++; }
      } else if (vertical) {
        let rr = r + 1;
        while (pez(rr, c)) { visto[rr * size + c] = true; largo++; rr++; }
      }
      tamanos.push(largo);
    }
  }
  if (tamanos.sort((a, b) => b - a).join(',') !== [...fleet].sort((a, b) => b - a).join(',')) {
    return false;
  }

  // las casillas reveladas tienen la forma que anunciaban
  for (let i = 0; i < size * size; i++) {
    const pista = revealed[i];
    if (pista === null) continue;
    if (formaEn(cells, size, i) !== pista) return false;
  }

  return true;
}

function isClosed(
  cells: CellState[],
  group: number[],
  size: number,
  rowShips: number[],
  colShips: number[],
  puzzle: EstanquePuzzle
): boolean {
  const agua = (r: number, c: number): boolean => {
    if (r < 0 || r >= size || c < 0 || c >= size) return true;
    const cell = cells[r * size + c];
    if (cell === false) return true;
    if (cell === true) return false;
    return rowShips[r] === puzzle.rowCounts[r] || colShips[c] === puzzle.colCounts[c];
  };

  const rows = group.map((i) => Math.floor(i / size));
  const cols = group.map((i) => i % size);
  const r1 = Math.min(...rows);
  const r2 = Math.max(...rows);
  const c1 = Math.min(...cols);
  const c2 = Math.max(...cols);

  if (group.length === 1) {
    return agua(r1 - 1, c1) && agua(r1 + 1, c1) && agua(r1, c1 - 1) && agua(r1, c1 + 1);
  }
  if (r1 === r2) return agua(r1, c1 - 1) && agua(r1, c2 + 1);
  return agua(r1 - 1, c1) && agua(r2 + 1, c1);
}

interface BoardAnalysis {
  errorCells: Set<number>;
  rowShips: number[];
  colShips: number[];
  /** Peces que faltan por encontrar, por tamaño. Negativo = sobran. */
  remaining: number[];
  /**
   * Peces cerrados que sin embargo incumplen alguna regla, por tamaño. No
   * descuentan de `remaining`, pero el modo asistido los señala: así el jugador
   * distingue "me falta esta pieza" de "la tengo puesta, pero mal".
   */
  conflicted: number[];
}

function analyzeBoard(puzzle: EstanquePuzzle, cells: CellState[]): BoardAnalysis {
  const { size, fleet } = puzzle;
  const ocupada = (i: number) => cells[i] === true;
  const errorCells = new Set<number>();
  const rowShips = new Array<number>(size).fill(0);
  const colShips = new Array<number>(size).fill(0);

  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (ocupada(r * size + c)) { rowShips[r]++; colShips[c]++; }
    }
  }

  for (let r = 0; r < size; r++) {
    if (rowShips[r] > puzzle.rowCounts[r]) {
      for (let c = 0; c < size; c++) if (ocupada(r * size + c)) errorCells.add(r * size + c);
    }
  }
  for (let c = 0; c < size; c++) {
    if (colShips[c] > puzzle.colCounts[c]) {
      for (let r = 0; r < size; r++) if (ocupada(r * size + c)) errorCells.add(r * size + c);
    }
  }

  // Contacto en diagonal: nunca permitido
  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size; c++) {
      const idx = r * size + c;
      if (!ocupada(idx)) continue;
      if (c > 0 && ocupada((r + 1) * size + c - 1)) {
        errorCells.add(idx); errorCells.add((r + 1) * size + c - 1);
      }
      if (c < size - 1 && ocupada((r + 1) * size + c + 1)) {
        errorCells.add(idx); errorCells.add((r + 1) * size + c + 1);
      }
    }
  }

  const maxPez = Math.max(...fleet);
  const remaining = new Array<number>(maxPez + 1).fill(0);
  for (const length of fleet) remaining[length]++;
  const conflicted = new Array<number>(maxPez + 1).fill(0);

  const visto = new Array<boolean>(size * size).fill(false);
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const start = r * size + c;
      if (!ocupada(start) || visto[start]) continue;

      const group: number[] = [];
      const pila = [start];
      visto[start] = true;
      while (pila.length) {
        const idx = pila.pop()!;
        group.push(idx);
        const gr = Math.floor(idx / size);
        const gc = idx % size;
        for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
          const nr = gr + dr;
          const nc = gc + dc;
          if (nr < 0 || nr >= size || nc < 0 || nc >= size) continue;
          const n = nr * size + nc;
          if (ocupada(n) && !visto[n]) { visto[n] = true; pila.push(n); }
        }
      }

      const rows = new Set(group.map((i) => Math.floor(i / size)));
      const cols = new Set(group.map((i) => i % size));
      const recto = rows.size === 1 || cols.size === 1;

      if (!recto || group.length > maxPez) {
        group.forEach((i) => errorCells.add(i));
      } else if (
        remaining[group.length] !== undefined &&
        // Una pieza solo se da por usada si además no incumple nada: cerrar una
        // forma no es lo mismo que colocarla bien, y tachar en la leyenda algo
        // que rompe los números le estaba diciendo al jugador "hecho" sobre una
        // posición imposible. Los errores que miramos aquí (línea pasada de
        // cuenta, contacto en diagonal) se ven en el tablero, así que esto no
        // adelanta nada de la solución.
        !group.some((i) => errorCells.has(i)) &&
        isClosed(cells, group, size, rowShips, colShips, puzzle)
      ) {
        remaining[group.length]--;
      } else if (
        remaining[group.length] !== undefined &&
        isClosed(cells, group, size, rowShips, colShips, puzzle)
      ) {
        conflicted[group.length]++;
      }
    }
  }

  return { errorCells, rowShips, colShips, remaining, conflicted };
}

// ------------------------------------------------------------- componente

export default function EstanqueGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const dif = parseInt(dificultad || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('estanque', dif);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  const [puzzle, setPuzzle] = useState<EstanquePuzzle | null>(null);
  const [cells, setCells] = useState<CellState[]>([]);
  const [history, setHistory] = useState<CellState[][]>([]);
  const [histIdx, setHistIdx] = useState(-1);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modoAsistido, setModoAsistido] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [moveCount, setMoveCount] = useState(0);
  const [forceNew, setForceNew] = useState(0);
  const [hintCell, setHintCell] = useState<number | null>(null);

  // Medimos el hueco disponible y de ahí sale el tamaño de casilla: así el
  // tablero encaja exacto en pantalla en vez de estimarlo con unidades de vista.
  const areaRef = useRef<HTMLDivElement>(null);
  const [area, setArea] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setArea({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  const pulsacionLarga = useRef<number | null>(null);
  const huboPulsacionLarga = useRef(false);

  useEffect(() => {
    if (isCompleted) return;
    const interval = setInterval(() => setElapsedTime((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [isCompleted]);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  /** Las casillas reveladas vienen ya puestas y no se pueden tocar. */
  const initialCells = (data: EstanquePuzzle): CellState[] =>
    data.revealed.map((hint) => (hint === null ? null : hint !== 'water'));

  const loadPuzzle = useCallback(async (isNew = false) => {
    setLoading(true);
    setError('');
    try {
      const urlSeed = !isNew ? searchParams.get('seed') : null;
      const seedToUse = urlSeed ? parseInt(urlSeed) : undefined;
      const response = await api.get('/estanque/generate', {
        params: { dificultad: dif, ...(seedToUse && { seed: seedToUse }) },
      });
      const data: EstanquePuzzle = response.data.data;
      setPuzzle(data);
      const start = initialCells(data);
      setCells(start);
      setHistory([[...start]]);
      setHistIdx(0);
      setMoveCount(0);
      setElapsedTime(0);
      setIsCompleted(false);
      setShowVictory(false);
      setModoAsistido(false);
      setHintCell(null);
      setSearchParams({ seed: data.seed.toString() }, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al generar el puzzle');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dif, forceNew]);

  useEffect(() => { loadPuzzle(forceNew > 0); }, [loadPuzzle]);

  const saveHistoryState = (next: CellState[]) => {
    const snap = [...next];
    const newHist = history.slice(0, histIdx + 1);
    newHist.push(snap);
    if (newHist.length > 200) newHist.shift();
    setHistory(newHist);
    setHistIdx(newHist.length - 1);
  };

  const applyCells = (next: CellState[], opciones?: { cuentaComoVictoria?: boolean }) => {
    if (!puzzle) return;
    setCells(next);
    setMoveCount((m) => m + 1);
    saveHistoryState(next);

    if (!tableroResuelto(puzzle, next)) return;

    setIsCompleted(true);

    // Rendirse y ver la solución cierra el tablero, pero no es una victoria:
    // no se celebra ni se guarda como partida resuelta.
    if (opciones?.cuentaComoVictoria === false) return;

    setShowVictory(true);
    saveGameProgress({
      tiempo: elapsedTime,
      movimientos: moveCount + 1,
      // Identifica el tablero jugado: sirve para no repetírselo al usuario.
      tablero_id: puzzle.seed,
    });
    if (isInSession) {
      saveSessionResult({ tiempo: elapsedTime, movimientos: moveCount + 1, completado: true });
    }
  };

  const editable = (index: number): boolean =>
    !!puzzle && !isCompleted && puzzle.revealed[index] === null;

  const onCellClick = (index: number) => {
    if (huboPulsacionLarga.current) { huboPulsacionLarga.current = false; return; }
    if (!editable(index)) return;
    const next = [...cells];
    const actual = next[index];
    next[index] = actual === null ? true : actual === true ? false : null;
    applyCells(next);
  };

  /** Clic derecho y pulsación larga: descartar directamente. */
  const alternarDescartada = (index: number) => {
    if (!editable(index)) return;
    const next = [...cells];
    next[index] = next[index] === false ? null : false;
    applyCells(next);
  };

  const onPointerDown = (index: number) => {
    if (!editable(index)) return;
    huboPulsacionLarga.current = false;
    pulsacionLarga.current = window.setTimeout(() => {
      huboPulsacionLarga.current = true;
      alternarDescartada(index);
    }, 400);
  };
  const cancelarPulsacion = () => {
    if (pulsacionLarga.current !== null) {
      clearTimeout(pulsacionLarga.current);
      pulsacionLarga.current = null;
    }
  };

  /** Tocar un número descarta todo lo que quede intacto en esa línea. */
  const descartarLinea = (tipo: 'fila' | 'columna', n: number) => {
    if (!puzzle || isCompleted) return;
    const { size } = puzzle;
    const next = [...cells];
    let tocadas = 0;
    for (let k = 0; k < size; k++) {
      const idx = tipo === 'fila' ? n * size + k : k * size + n;
      if (puzzle.revealed[idx] !== null) continue;
      if (next[idx] === null) { next[idx] = false; tocadas++; }
    }
    if (tocadas > 0) applyCells(next);
  };

  const onUndo = () => {
    if (histIdx <= 0) return;
    setCells([...history[histIdx - 1]]);
    setHistIdx(histIdx - 1);
  };

  const onRedo = () => {
    if (histIdx >= history.length - 1) return;
    setCells([...history[histIdx + 1]]);
    setHistIdx(histIdx + 1);
  };

  const onReset = () => {
    if (!puzzle || !window.confirm('¿Reiniciar el estanque?')) return;
    const start = initialCells(puzzle);
    setCells(start);
    saveHistoryState(start);
    setIsCompleted(false);
    setShowVictory(false);
    setHintCell(null);
  };

  const onNewBoard = () => {
    setSearchParams({});
    setForceNew((f) => f + 1);
  };

  const handleHint = async () => {
    if (!puzzle || isCompleted) return;
    try {
      const { data: { data: hint } } = await api.post('/estanque/hint', {
        dificultad: puzzle.difficulty,
        seed: puzzle.seed,
        cells,
      });
      if (hint && hint.index !== null) {
        const next = [...cells];
        next[hint.index] = hint.value === 'ship';
        setHintCell(hint.index);
        setTimeout(() => setHintCell(null), 1200);
        applyCells(next);
      }
    } catch { /* ignore */ }
  };

  const handleShowSolution = async () => {
    if (!puzzle || !window.confirm('¿Estás seguro? Esto mostrará la solución completa y la partida no contará como resuelta.')) return;
    try {
      const { data: { data } } = await api.post('/estanque/solution', {
        dificultad: puzzle.difficulty,
        seed: puzzle.seed,
      });
      const solucion = data.solution as boolean[];
      applyCells(solucion.map((hay) => hay), { cuentaComoVictoria: false });
    } catch {
      setError('No se pudo recuperar la solución');
    }
  };

  if (loading) {
    return (
      <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Generando estanque…</div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-4xl mb-4">⚠️</div>
          <p>{error || 'Error desconocido'}</p>
          <button onClick={onNewBoard} className="mt-4 px-6 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg">
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  const { size } = puzzle;
  const { errorCells, rowShips, colShips, remaining, conflicted } = analyzeBoard(puzzle, cells);
  const errores = modoAsistido ? errorCells : new Set<number>();

  // El tablero es cuadrado y ocupa el menor de los dos lados disponibles,
  // descontando el marco y el sitio de la leyenda. El +1 son las cabeceras.
  const dispW = Math.max(area.w - PAD_MARCO * 2, 0);
  const dispH = Math.max(area.h - PAD_MARCO * 2, 0);

  // Las dos colocaciones posibles de la leyenda, y nos quedamos con la que
  // deja el tablero más grande: en apaisado gana el panel lateral, en vertical
  // la franja de abajo, sin tener que fijar un ancho de corte a ojo.
  const ladoLateral = Math.min(dispW - ANCHO_PANEL - HUECO_LEYENDA, dispH);
  const ladoAbajo = Math.min(dispW, dispH - ALTO_LEYENDA - HUECO_LEYENDA);
  const leyendaLateral = ladoLateral >= ladoAbajo;

  const lado = Math.max(leyendaLateral ? ladoLateral : ladoAbajo, 0);
  const cellPx = Math.max(Math.floor(lado / (size + 1)), 16);
  const detalle: 'grande' | 'simplificada' = cellPx >= PX_DETALLE ? 'grande' : 'simplificada';
  const grosorRejilla = 200 / cellPx; // ~1px reales, en unidades del viewBox

  const tamanos = Array.from(new Set(puzzle.fleet)).sort((a, b) => b - a);

  // La flota se dibuja pieza a pieza y cada una se tacha al colocarla, en vez
  // de un contador que baja en silencio: un número que pasa de 3 a 2 obliga a
  // recordar en cuánto estaba, y lo que el jugador necesita ver es qué le queda.
  const flota = puzzle.fleet.reduce<Record<number, number>>((acc, l) => {
    acc[l] = (acc[l] || 0) + 1;
    return acc;
  }, {});
  const grupos = tamanos.map((largo) => {
    const total = flota[largo];
    const quedan = remaining[largo];
    const usadas = Math.min(total, total - quedan);
    // El aviso de "cerrada pero mal" solo con el escudo: sin él la leyenda se
    // limita a no tachar, que ya es lo justo para no confirmar algo roto.
    const enConflicto = modoAsistido
      ? Math.min(conflicted[largo] ?? 0, total - usadas)
      : 0;
    return { largo, total, usadas, enConflicto, sobran: Math.max(0, -quedan) };
  });

  // El tamaño de pieza sale del grupo más ancho, que es el que decide si cabe
  // todo: nos quedamos con el mayor que no desborde ni el panel ni la franja.
  const anchoGrupo = leyendaLateral
    ? ANCHO_PANEL - PAD_MARCO * 2 - 20
    : lado / 2 - 28;
  const unidadesMax = Math.max(...grupos.map((g) => g.largo * g.total));
  const huecosMax = Math.max(...grupos.map((g) => g.total - 1));
  const pxPieza = Math.max(
    10,
    Math.min(
      leyendaLateral ? 30 : 24,
      Math.floor((anchoGrupo - huecosMax * 5) / unidadesMax)
    )
  );
  const pxTexto = Math.max(12, Math.round(pxPieza * 0.7));

  const leyenda = (
    <div
      style={
        leyendaLateral
          ? { width: ANCHO_PANEL, backgroundColor: MARCO_TABLERO, borderRadius: 14, padding: PAD_MARCO }
          : { height: ALTO_LEYENDA, marginTop: HUECO_LEYENDA, width: lado }
      }
      className={
        leyendaLateral
          ? 'flex flex-col items-start justify-center gap-3 shrink-0 self-stretch'
          : 'grid grid-cols-2 content-center justify-items-start gap-x-4 gap-y-1'
      }
    >
      {leyendaLateral && (
        <span className="font-bold tracking-wider uppercase self-center"
          style={{ color: '#8FA8B2', fontSize: 12 }}>
          Habitantes
        </span>
      )}
      {grupos.map(({ largo, total, usadas, enConflicto, sobran }) => (
        <div key={largo} className="flex items-center gap-2"
          title={`${largo === 1 ? 'Nenúfares' : `Carpas de ${largo} casillas`}: ${
            usadas === total ? 'todas colocadas' : `te faltan ${total - usadas}`
          }${enConflicto ? `, ${enConflicto} cerrada(s) pero incumpliendo alguna regla` : ''}${
            sobran ? `, y te sobran ${sobran}` : ''
          }`}>
          <span className="font-bold tabular-nums shrink-0"
            style={{ color: sobran ? '#FCA5A5' : '#8FA8B2', fontSize: pxTexto, width: pxTexto }}>
            {largo}
          </span>
          <span className="flex" style={{ gap: 5 }}>
            {Array.from({ length: total }, (_, u) => {
              const puesta = u < usadas;
              const conflictiva = !puesta && u < usadas + enConflicto;
              return (
                // El tachado va fuera del envoltorio atenuado: dentro heredaba
                // la opacidad de la pieza y no se veía.
                <span key={u} className="flex relative"
                  style={conflictiva ? {
                    outline: '2px solid #DC2626', outlineOffset: 1, borderRadius: 3,
                  } : undefined}>
                  {/* Gris en vez de transparente: atenuar sin más se comía el
                      verde del nenúfar contra el fondo del panel. En gris la
                      silueta se sigue reconociendo y aun así se lee "gastada". */}
                  <span className="flex"
                    style={puesta ? { filter: 'grayscale(1)', opacity: 0.6 } : undefined}>
                  {Array.from({ length: largo }, (_, k) => {
                    const clave =
                      largo === 1
                        ? 'simplificada/nenufar'
                        : k === 0
                          ? 'simplificada/horizontal/cabeza'
                          : k === largo - 1
                            ? 'simplificada/horizontal/cola'
                            : 'simplificada/horizontal/central';
                    return (
                      <svg key={k} viewBox="0 0 100 100" width={pxPieza} height={pxPieza}
                        style={{ display: 'block' }}>
                        {PIEZAS[clave]}
                      </svg>
                    );
                  })}
                  </span>
                  {puesta && (
                    <span style={{
                      position: 'absolute', left: -2, right: -2, top: '50%',
                      height: 2, marginTop: -1, borderRadius: 1,
                      backgroundColor: '#F3F6F7',
                    }} />
                  )}
                </span>
              );
            })}
          </span>
          {sobran > 0 && (
            <span className="font-bold tabular-nums shrink-0"
              style={{ color: '#FCA5A5', fontSize: pxTexto }}>
              +{sobran}
            </span>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex flex-col">
      <style>{`
        @keyframes estanque-pista { 0%,100% { opacity: 1 } 50% { opacity: .3 } }
        .estanque-pista { animation: estanque-pista .6s ease-in-out 2 }
        @media (prefers-reduced-motion: reduce) { .estanque-pista { animation: none } }
      `}</style>

      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🎏 Estanque</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map((d) => (
            <button
              key={d.orden}
              onClick={() => { if (dif === d.orden) onNewBoard(); else navigate(`/juegos/estanque/${d.orden}`); }}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dif === d.orden ? 'bg-white text-purple-700' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <span className="text-white/70 text-xs hidden sm:inline">⏱ {formatTime(elapsedTime)}</span>
        <div className="flex gap-1 items-center">
          <button onClick={onUndo} disabled={histIdx <= 0} title="Deshacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
          </button>
          <button onClick={onRedo} disabled={histIdx >= history.length - 1} title="Rehacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/></svg>
          </button>
          <button onClick={() => setModoAsistido((v) => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded-md transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          <button onClick={handleHint} disabled={isCompleted} title="Pista"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17H8v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/></svg>
          </button>
          <button onClick={handleShowSolution} disabled={isCompleted} title="Ver solución"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
          <button onClick={onReset} title="Reiniciar"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          </button>
          <button onClick={onNewBoard} title="Nuevo estanque"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </button>
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-3 mt-2 p-2 bg-blue-100 border border-blue-300 text-blue-800 rounded-lg text-sm text-center">
          {error}
        </div>
      )}

      <div
        ref={areaRef}
        className="flex-1 min-h-0 flex items-center justify-center p-2"
        style={{ gap: leyendaLateral ? HUECO_LEYENDA : 0 }}
      >
        <div
          style={{
            backgroundColor: MARCO_TABLERO,
            borderRadius: 14,
            padding: PAD_MARCO,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${size + 1}, ${cellPx}px)`,
              gridTemplateRows: `repeat(${size + 1}, ${cellPx}px)`,
            }}
          >
            {Array.from({ length: (size + 1) * (size + 1) }, (_, pos) => {
              const gr = Math.floor(pos / (size + 1));
              const gc = pos % (size + 1);

              if (gr === 0 && gc === 0) {
                return <div key={pos} style={{ width: cellPx, height: cellPx }} />;
              }

              // Cabeceras con los números
              if (gr === 0 || gc === 0) {
                const esFila = gc === 0;
                const n = esFila ? gr - 1 : gc - 1;
                const objetivo = esFila ? puzzle.rowCounts[n] : puzzle.colCounts[n];
                const puestos = esFila ? rowShips[n] : colShips[n];
                // El color de los números es una ayuda: solo con el modo asistido.
                const fondo = !modoAsistido
                  ? 'transparent'
                  : puestos === objetivo ? '#16A34A' : puestos > objetivo ? '#DC2626' : 'transparent';
                return (
                  <div key={pos} style={{ width: cellPx, height: cellPx }}
                    className="flex items-center justify-center">
                    <button
                      onClick={() => descartarLinea(esFila ? 'fila' : 'columna', n)}
                      title="Descartar el resto de esta línea"
                      style={{
                        width: cellPx * 0.72, height: cellPx * 0.72,
                        borderRadius: '50%', backgroundColor: fondo,
                        color: '#F3F6F7', fontWeight: 700,
                        fontSize: cellPx * 0.46, lineHeight: 1,
                      }}
                      className="flex items-center justify-center transition-colors"
                    >
                      {objetivo}
                    </button>
                  </div>
                );
              }

              const r = gr - 1;
              const c = gc - 1;
              const i = r * size + c;
              const pista = puzzle.revealed[i];
              const bloqueada = pista !== null;
              const enError = errores.has(i);
              const esPista = hintCell === i;

              let fondoClave: string;
              if (enError) fondoClave = 'error';
              else if (esPista) fondoClave = 'pista';
              else if (bloqueada) fondoClave = pista === 'water' ? 'revelada-agua' : 'revelada';
              else if (cells[i] === false) fondoClave = 'descartada';
              else fondoClave = 'intacta';

              const marcoClave = enError ? 'error' : esPista ? 'pista' : bloqueada ? 'revelada' : null;
              const piezaClave = claveDePieza(puzzle, cells, i, detalle);

              return (
                <button
                  key={pos}
                  onClick={() => onCellClick(i)}
                  onContextMenu={(e) => { e.preventDefault(); alternarDescartada(i); }}
                  onPointerDown={() => onPointerDown(i)}
                  onPointerUp={cancelarPulsacion}
                  onPointerLeave={cancelarPulsacion}
                  onPointerCancel={cancelarPulsacion}
                  disabled={isCompleted}
                  style={{
                    width: cellPx, height: cellPx, padding: 0, border: 0,
                    cursor: bloqueada || isCompleted ? 'default' : 'pointer',
                    touchAction: 'manipulation',
                  }}
                  className={esPista ? 'estanque-pista' : undefined}
                >
                  <svg viewBox="0 0 100 100" width={cellPx} height={cellPx} style={{ display: 'block' }}>
                    {FONDOS[`${detalle}/${fondoClave}`]}
                    <rect x="0" y="0" width="100" height="100" fill="none"
                      stroke={REJILLA} strokeWidth={grosorRejilla} />
                    {piezaClave && PIEZAS[piezaClave]}
                    {marcoClave && MARCOS[`${detalle}/${marcoClave}`]}
                  </svg>
                </button>
              );
            })}
          </div>

          {!leyendaLateral && leyenda}
        </div>

        {leyendaLateral && leyenda}
      </div>

      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{ tiempo: elapsedTime, movimientos: moveCount }}
      />

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Estanque"
        gameIcon="🎏"
        objective="Descubre dónde se esconden las carpas y los nenúfares. Los números dicen cuántas casillas ocupadas hay en cada fila y columna."
        rules={[
          'Cada carpa ocupa una línea recta, horizontal o vertical, de casillas seguidas. Cada nenúfar ocupa una sola casilla.',
          'Las carpas y los nenúfares nunca se tocan, ni siquiera en diagonal.',
          'Los números de cada fila y columna indican cuántas casillas están ocupadas.',
          'Las casillas con olas son agua: ahí no hay nada. Las marcas tú al descartar.',
          'Las casillas con borde marrón son pistas fijas: agua, un nenúfar o un trozo de carpa.',
          <>
            <strong>La lista de habitantes</strong> dice de cuántas casillas es cada carpa y cuántos
            hay de cada tipo. Cada uno se tacha cuando lo tienes cerrado con agua por las dos puntas
            y no incumple ningún número. Si lo colocas bien pero todavía no has marcado el agua de
            alrededor, aún no se tacha.
          </>,
          <>
            <strong>Sin el escudo</strong>, la lista no avisa de nada más: si algo está mal puesto,
            simplemente no se tacha, y te toca averiguar por qué.{' '}
            <strong>Con el escudo</strong>, un habitante cerrado que incumpla alguna regla aparece
            con contorno rojo — lo tienes puesto, pero mal. El escudo nunca cambia qué se tacha,
            solo explica lo que no se ha tachado.
          </>,
        ]}
        howToPlay="Toca una casilla para cambiarla: vacía → ocupada → agua → vacía. Con el clic derecho, o dejando el dedo pulsado, la marcas como agua directamente. Toca un número para llenar de agua el resto de esa fila o columna. Las carpas se dibujan solas a partir de las casillas que marcas y crecen según las alargas. El escudo activa el modo asistido: resalta en rojo las casillas que incumplen alguna regla y colorea los números de las líneas ya completas."
        author="Basado en el clásico Bimaru"
      />
    </div>
  );
}
